import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { queryClient } from './lib/queryClient';
import { initPwa } from './lib/sw';
// Side-effect import: registers the `beforeinstallprompt` listener at
// module-load time so we don't miss it before React mounts. The actual
// hook is consumed inside the Today screen Tools drawer.
import './lib/useInstallPrompt';
import { initTelemetry } from './lib/telemetry';
import { initCwv } from './lib/cwv';
import './styles/globals.css';
import './styles/utilities.css';
import './styles/components.css';
import './styles/landing.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

// QueryClientProvider lives in main.tsx (NOT routes.tsx) so a concurrent
// route-splitting agent can edit routes without touching this wrapper.
// The provider has to be above every screen — it wraps <App /> which
// owns <BrowserRouter> + <AppRoutes />.
createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);

// launchQueue consumer — paired with manifest `launch_handler.client_mode:
// focus-existing`. The browser focuses our existing PWA window without
// auto-navigating; we receive the launch URL via the queue and dispatch
// a 'qk:launched' CustomEvent so AuthedRoutes (which owns useNavigate)
// can react in-SPA. Chromium-only (Chrome 110+); Safari/Firefox ignore
// both the manifest key and the queue API and just open a new window.
//
// The handler intentionally just forwards the URL — all of the
// "what to do with this URL" logic lives in routes.tsx so it stays
// next to the rest of the routing decisions.
interface LaunchParamsLike { targetURL?: string }
interface LaunchQueueLike { setConsumer(consumer: (params: LaunchParamsLike) => void): void }
const launchQueue = (window as Window & { launchQueue?: LaunchQueueLike }).launchQueue;
if (launchQueue) {
  try {
    launchQueue.setConsumer((params) => {
      if (!params.targetURL) return;
      window.dispatchEvent(
        new CustomEvent('qk:launched', { detail: { url: params.targetURL } }),
      );
    });
  } catch {
    // Best-effort. A failed consumer registration falls back to the
    // browser's default (open new window) which is benign.
  }
}

// Side-effect: register the service worker (prod only) and start
// listening for `online` to drain the offline mutation queue.
initPwa();
// Telemetry sink — inert unless VITE_SENTRY_DSN is set in Pages env.
initTelemetry();
// Real-user CWV (LCP/INP/CLS/FCP/TTFB) → POST /api/analytics/cwv on
// pagehide. Honors `navigator.connection.saveData` (drops the beacon
// when the user opted into reduced data).
initCwv();
